/**
 * Atom Categories Megamenu Component
 * Handles the interactive functionality of the categories megamenu
 * Dynamically loads and renders category data from /pages/categories/webcat
 */
class AtomCategoriesMegaMenu {
  constructor(element) {
    this.element = element;
    this.storageKey = 'atom-megamenu-category-type';
    this.dataUrl = '/pages/categories';
    this.categoriesData = null;
    this.isLoaded = false;
    this.debtorID = window.ATOM.customer?.metafields?.punchout?.debtor_id;

    // Cache template references
    this.templates = {
      toggleButton: document.getElementById('atom-megamenu-toggle-button-template'),
      categoryContent: document.getElementById('atom-megamenu-category-content-template'),
      categoryButton: document.getElementById('atom-megamenu-category-button-template'),
      subcategory: document.getElementById('atom-megamenu-subcategory-template'),
      subcategoryGroup: document.getElementById('atom-megamenu-subcategory-group-template'),
      subcategoryItem: document.getElementById('atom-megamenu-subcategory-item-template'),
      loading: document.getElementById('atom-megamenu-loading-template'),
      error: document.getElementById('atom-megamenu-error-template'),
    };

    this.init();
  }

  async init() {
    // Show loading state
    this.showLoadingState();

    try {
      // Load categories data
      await this.loadCategoriesData();

      // Build the menu from data
      this.buildMenu();

      // Now bind events and setup
      this.bindEvents();
      this.setupAccessibility();

      // Find and set the active category type
      this.setCategoryType();

      // Wait a moment for DOM to be fully ready, then set initial state
      setTimeout(() => {
        this.resetCategorySelection();
      }, 50);

      this.isLoaded = true;
    } catch (error) {
      console.error('AtomCategoriesMegaMenu: Failed to initialize:', error);
      this.showErrorState();
    }
  }

  showLoadingState() {
    const container = this.element.querySelector('.atom-megamenu__categories-container');
    if (container && this.templates.loading) {
      const loadingEl = this.templates.loading.content.cloneNode(true);
      container.appendChild(loadingEl);
    }
  }

  showErrorState() {
    const container = this.element.querySelector('.atom-megamenu__categories-container');
    if (container) {
      container.innerHTML = '';
      if (this.templates.error) {
        const errorEl = this.templates.error.content.cloneNode(true);
        container.appendChild(errorEl);
      }
    }
  }

  async loadCategoriesData() {
    const response = await fetch(this.dataUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch categories: ${response.status}`);
    }

    // The endpoint returns JSON data but with text/html content-type
    // so we need to parse the text as JSON manually
    const text = await response.text();
    const data = JSON.parse(text);
    this.categoriesData = data;
    return data;
  }

  buildMenu() {
    if (!this.categoriesData || !Array.isArray(this.categoriesData)) {
      console.error('AtomCategoriesMegaMenu: Invalid categories data');
      this.showErrorState();
      return;
    }

    // Clear loading state
    const container = this.element.querySelector('.atom-megamenu__categories-container');
    if (container) {
      container.innerHTML = '';
    }

    // Build toggle buttons
    this.buildToggleButtons();

    // Build category content for each category type
    this.categoriesData.forEach((categoryType, index) => {
      this.buildCategoryContent(categoryType, index === 0);
    });
  }

  buildToggleButtons() {
    const toggleContainer = this.element.querySelector('.atom-megamenu__toggle');
    if (!toggleContainer || !this.templates.toggleButton) return;

    toggleContainer.innerHTML = '';

    // Sort to put webcat first
    const sortedData = [...this.categoriesData].sort((a, b) => {
      if (a.code === 'webcat') return -1;
      if (b.code === 'webcat') return 1;
      return 0;
    });

    sortedData.forEach((categoryType, index) => {
      const buttonEl = this.templates.toggleButton.content.cloneNode(true);
      const button = buttonEl.querySelector('.atom-megamenu__toggle-button');

      const typeId = this.handleize(categoryType.code);
      button.dataset.categoryType = typeId;
      button.textContent = this.unEscapeHtml(categoryType.name);

      // Set first button (webcat) as active
      if (index === 0) {
        button.classList.add('atom-megamenu__toggle-button--active');
        button.setAttribute('aria-pressed', 'true');
      }

      toggleContainer.appendChild(buttonEl);
    });
  }

  buildCategoryContent(categoryType, isFirst) {
    const container = this.element.querySelector('.atom-megamenu__categories-container');
    if (!container || !this.templates.categoryContent) return;

    const contentEl = this.templates.categoryContent.content.cloneNode(true);
    const contentWrapper = contentEl.querySelector('.atom-megamenu__category-content');

    const typeId = this.handleize(categoryType.code);
    contentWrapper.dataset.categoryType = typeId;

    // Show first category type by default
    if (isFirst) {
      contentWrapper.classList.remove('atom-megamenu__category-content--hidden');
    }

    // Get the categories
    const categories = categoryType.categories || [];

    // Build left panel (category buttons)
    const categoriesList = contentEl.querySelector('.atom-megamenu__categories-list');
    if (categoriesList) {
      categories.forEach((category, index) => {
        if (!this.debtorID || !category.db?.includes(this.debtorID)) this.buildCategoryButton(categoriesList, category, index);
      });
    }

    // Build right panel (subcategories)
    const subcategoriesContainer = contentEl.querySelector('.atom-megamenu__subcategories');
    if (subcategoriesContainer) {
      categories.forEach((category, index) => {
        if (!this.debtorID || !category.db?.includes(this.debtorID)) this.buildSubcategoryPanel(subcategoriesContainer, category, index);
      });
    }

    container.appendChild(contentEl);
  }

  buildCategoryButton(container, category, index) {
    if (!this.templates.categoryButton) return;

    const buttonEl = this.templates.categoryButton.content.cloneNode(true);
    const button = buttonEl.querySelector('.atom-megamenu__category-button');

    const categoryId = this.handleize(category.title);
    button.dataset.category = categoryId;
    button.dataset.categoryUrl = category.url || '';
    button.dataset.categoryIndex = index.toString();
    button.textContent = this.unEscapeHtml(category.title);

    // Set first button as active
    if (index === 0) {
      button.classList.add('atom-megamenu__category-button--active');
      button.setAttribute('aria-expanded', 'true');
    }

    container.appendChild(buttonEl);
  }

  buildSubcategoryPanel(container, category, index) {
    if (!this.templates.subcategory) return;

    const panelEl = this.templates.subcategory.content.cloneNode(true);
    const panel = panelEl.querySelector('.atom-megamenu__subcategory');

    const categoryId = this.handleize(category.title);
    panel.dataset.category = categoryId;
    panel.dataset.categoryIndex = index.toString();

    // Set first panel as active
    if (index === 0) {
      panel.classList.add('atom-megamenu__subcategory--active');
    }

    const contentContainer = panelEl.querySelector('.atom-megamenu__subcategory-content');

    if (category.children && category.children.length > 0) {
      // Has children - build subcategory groups
      category.children.forEach((subcategory) => {
        if (!this.debtorID || !subcategory.db?.includes(this.debtorID)) this.buildSubcategoryGroup(contentContainer, subcategory);
      });
    } else {
      // No children - just show the category link
      this.buildSubcategoryGroup(contentContainer, {
        title: category.title,
        url: category.url,
        children: [],
      });
    }

    container.appendChild(panelEl);
  }

  buildSubcategoryGroup(container, subcategory) {
    if (!this.templates.subcategoryGroup) return;

    const groupEl = this.templates.subcategoryGroup.content.cloneNode(true);
    const titleLink = groupEl.querySelector('.atom-megamenu__subcategory-link');

    titleLink.href = subcategory.url || '#';
    titleLink.textContent = this.unEscapeHtml(subcategory.title);

    const itemsList = groupEl.querySelector('.atom-megamenu__subcategory-list');

    if (subcategory.children && subcategory.children.length > 0) {
      subcategory.children.forEach((grandchild) => {
        if (!this.debtorID || !grandchild.db?.includes(this.debtorID)) this.buildSubcategoryItem(itemsList, grandchild);
      });
    } else {
      // Remove empty list if no children
      itemsList.remove();
    }

    container.appendChild(groupEl);
  }

  buildSubcategoryItem(container, item) {
    if (!this.templates.subcategoryItem) return;

    const itemEl = this.templates.subcategoryItem.content.cloneNode(true);
    const link = itemEl.querySelector('.atom-megamenu__subcategory-item-link');

    link.href = item.url || '#';
    link.textContent = this.unEscapeHtml(item.title);

    container.appendChild(itemEl);
  }

  // Utility function to convert string to handle format
  handleize(str) {
    if (!str) return '';
    return str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  // Utility function to unescape HTML entities - textContent will re-escape them
  unEscapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.innerHTML = str;
    return div.textContent;
  }

  bindEvents() {
    // Handle all trigger buttons (both primary and secondary)
    const triggerButtons = document.querySelectorAll('.atom-megamenu__trigger');

    // Ensure all triggers have proper ARIA attributes and sync their states
    triggerButtons.forEach((trigger, index) => {
      const triggerId = `AtomCategoriesMegaMenu-Trigger-${index + 1}`;

      // Set unique IDs for each trigger
      trigger.id = triggerId;
      trigger.setAttribute('aria-controls', 'AtomCategoriesMegaMenu-Content');

      // Sync the aria-expanded state with the megamenu state
      const updateTriggerState = () => {
        const isOpen = this.isMegamenuOpen();
        trigger.setAttribute('aria-expanded', isOpen.toString());
      };

      // Update state initially
      updateTriggerState();

      // Listen for changes to the megamenu state
      this.element.addEventListener('toggle', updateTriggerState);
    });

    // Handle category type toggle
    const toggleButtons = this.element.querySelectorAll('.atom-megamenu__toggle-button');
    toggleButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.switchCategoryType(button);
      });
    });

    // Handle category button hover for left panel (desktop only)
    const categoryButtons = this.element.querySelectorAll('.atom-megamenu__category-button');
    categoryButtons.forEach((button) => {
      button.addEventListener('mouseenter', () => {
        // Only handle hover on desktop (larger screens)
        if (window.innerWidth > 989) {
          this.showCategory(button);
        }
      });

      // Handle click
      button.addEventListener('click', (e) => {
        if (window.innerWidth > 989) {
          // Desktop: navigate to category page
          const categoryUrl = button.dataset.categoryUrl;
          if (categoryUrl) {
            window.location.href = categoryUrl;
          }
        } else {
          // Mobile: prevent default, show subcategories instead
          e.preventDefault();
          e.stopPropagation();
          this.showSubcategoriesOnMobile(button);
        }
      });
    });

    // Handle subcategory container hover to keep content visible (desktop only)
    const subcategoryContainers = this.element.querySelectorAll('.atom-megamenu__subcategories');
    subcategoryContainers.forEach((container) => {
      container.addEventListener('mouseenter', () => {
        // Only handle hover on desktop (larger screens)
        if (window.innerWidth > 989) {
          // Keep the current active category visible
          const activeButton = this.element.querySelector('.atom-megamenu__category-button--active');
          if (activeButton) {
            this.showCategory(activeButton);
          }
        }
      });
    });

    // Handle back button click for mobile navigation
    const backButtons = this.element.querySelectorAll('.atom-megamenu__back-button');
    backButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.hideSubcategoriesOnMobile();
      });
    });

    // Handle click outside to close megamenu
    // Use a single, consolidated approach with proper timing
    let clickOutsideTimeout;

    document.addEventListener('click', (e) => {
      // Clear any existing timeout
      if (clickOutsideTimeout) {
        clearTimeout(clickOutsideTimeout);
      }

      // Only process if megamenu is open
      if (!this.isMegamenuOpen()) {
        return;
      }

      // Check if click is outside the megamenu
      if (!this.element.contains(e.target)) {
        // Check if the click target or any of its parents is a trigger button
        const isTriggerClick = this.isClickOnTrigger(e.target);

        if (isTriggerClick) {
          return;
        }

        // Add a small delay to prevent immediate closing when opening the menu
        clickOutsideTimeout = setTimeout(() => {
          this.closeMegamenu();
        }, 100); // 100ms delay
      }
    });

    // Handle escape key to close megamenu
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeMegamenu();
      }
    });

    // Handle window resize to reset mobile view when switching to desktop
    window.addEventListener('resize', () => {
      this.handleWindowResize();
    });
  }

  setupAccessibility() {
    // Ensure proper ARIA attributes
    const categoryButtons = this.element.querySelectorAll('.atom-megamenu__category-button');
    categoryButtons.forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
  }

  showCategory(button) {
    const categoryType = button.closest('[data-category-type]');
    if (!categoryType) return;

    const categoryId = button.dataset.category;

    // Remove active state from all buttons in this category type
    const allButtons = categoryType.querySelectorAll('.atom-megamenu__category-button');
    allButtons.forEach((btn) => {
      btn.classList.remove('atom-megamenu__category-button--active');
      btn.setAttribute('aria-expanded', 'false');
    });

    // Hide all subcategories in this category type
    const allSubcategories = categoryType.querySelectorAll('.atom-megamenu__subcategory');
    allSubcategories.forEach((sub) => {
      sub.classList.remove('atom-megamenu__subcategory--active');
    });

    // Activate current button and show corresponding subcategory
    button.classList.add('atom-megamenu__category-button--active');
    button.setAttribute('aria-expanded', 'true');

    const targetSubcategory = categoryType.querySelector(`.atom-megamenu__subcategory[data-category="${categoryId}"]`);
    if (targetSubcategory) {
      targetSubcategory.classList.add('atom-megamenu__subcategory--active');
    }
  }

  resetCategorySelection() {
    // Reset all category buttons to inactive
    const allButtons = this.element.querySelectorAll('.atom-megamenu__category-button');
    allButtons.forEach((button) => {
      button.classList.remove('atom-megamenu__category-button--active');
      button.setAttribute('aria-expanded', 'false');
    });

    // Hide all subcategories
    const allSubcategories = this.element.querySelectorAll('.atom-megamenu__subcategory');
    allSubcategories.forEach((sub) => {
      sub.classList.remove('atom-megamenu__subcategory--active');
    });

    // Show first category by default in the active wrapper
    const activeWrapper = this.element.querySelector('.atom-megamenu__category-content:not(.atom-megamenu__category-content--hidden)');

    if (activeWrapper) {
      const firstButton = activeWrapper.querySelector('.atom-megamenu__category-button');
      const firstSubcategory = activeWrapper.querySelector('.atom-megamenu__subcategory');

      if (firstButton && firstSubcategory) {
        firstButton.classList.add('atom-megamenu__category-button--active');
        firstButton.setAttribute('aria-expanded', 'true');
        firstSubcategory.classList.add('atom-megamenu__subcategory--active');
      }
    }
  }

  closeMegamenu() {
    // Reset mobile view back to category list if on mobile
    if (window.innerWidth <= 749) {
      this.hideSubcategoriesOnMobile();
    }

    // Close the details element (the megamenu element itself)
    if (this.element.tagName.toLowerCase() === 'details' && this.element.hasAttribute('open')) {
      this.element.removeAttribute('open');
    }
  }

  isMegamenuOpen() {
    // Check if the megamenu element itself is a details element
    const isDetailsElement = this.element.tagName.toLowerCase() === 'details';
    const hasOpenAttribute = this.element.hasAttribute('open');
    const isOpen = isDetailsElement && hasOpenAttribute;

    return isOpen;
  }

  handleWindowResize() {
    // If switching from mobile to desktop, reset mobile view state
    if (window.innerWidth > 749) {
      // Check if we were previously in mobile subcategory view
      const hasMobileSubcategoriesVisible = this.element.querySelector('.atom-megamenu__subcategories--mobile-visible');
      if (hasMobileSubcategoriesVisible) {
        this.resetMobileView();
      }
    }
  }

  // Method to update all trigger states (internal and external)
  updateAllTriggerStates() {
    const isOpen = this.isMegamenuOpen();

    // Update all summary triggers
    const summaryTriggers = document.querySelectorAll('.atom-megamenu__trigger');
    summaryTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', isOpen.toString());
    });
  }

  // Test method to manually open and close the megamenu
  doOpenClose() {
    const currentState = this.isMegamenuOpen();

    if (currentState) {
      this.closeMegamenu();
    } else {
      this.element.setAttribute('open', '');
    }
  }

  // Method to check if a click target or any of its parents is a trigger button
  isClickOnTrigger(target) {
    if (!target) return false;

    // Check if the target itself is a trigger
    if (target.classList && target.classList.contains('atom-megamenu__trigger')) {
      return true;
    }

    // Check if any parent element is a trigger
    let currentElement = target.parentElement;
    while (currentElement && currentElement !== document.body) {
      if (currentElement.classList && currentElement.classList.contains('atom-megamenu__trigger')) {
        return true;
      }
      currentElement = currentElement.parentElement;
    }

    // Check if any parent element has the atom-megamenu class (the details element itself)
    currentElement = target.parentElement;
    while (currentElement && currentElement !== document.body) {
      if (currentElement.classList && currentElement.classList.contains('atom-megamenu')) {
        return true;
      }
      currentElement = currentElement.parentElement;
    }

    return false;
  }

  switchCategoryType(clickedButton, save = true) {
    const categoryType = clickedButton.dataset.categoryType;

    // Remember which type was active before the buttons below are updated
    const activeButton = this.element.querySelector('.atom-megamenu__toggle-button--active');
    const previousType = activeButton ? activeButton.dataset.categoryType : null;

    // Update toggle button states
    const allToggleButtons = this.element.querySelectorAll('.atom-megamenu__toggle-button');
    allToggleButtons.forEach((button) => {
      button.classList.remove('atom-megamenu__toggle-button--active');
      button.setAttribute('aria-pressed', 'false');
    });

    clickedButton.classList.add('atom-megamenu__toggle-button--active');
    clickedButton.setAttribute('aria-pressed', 'true');

    // Show/hide main content wrappers (webcat vs UNSPSC)
    const allMainContentWrappers = this.element.querySelectorAll('.atom-megamenu__category-content[data-category-type]');

    allMainContentWrappers.forEach((wrapper) => {
      if (wrapper.dataset.categoryType === categoryType) {
        wrapper.classList.remove('atom-megamenu__category-content--hidden');
      } else {
        wrapper.classList.add('atom-megamenu__category-content--hidden');
      }
    });

    if (save) {
      console.log('Saving category type');
      // Save the selected category type to localStorage
      this.saveCategoryType(categoryType);
    }

    // Dispatch event for Algolia search to update hierarchical menu visibility.
    // Only announce a real change: the menu marks its first toggle button active while it
    // builds, so a page load that stays on that same type no longer re-announces it, and
    // the search page ignores any announcement that does not change the type.
    if (previousType !== categoryType) {
      window.dispatchEvent(
        new CustomEvent('atom-category-type-changed', {
          detail: { categoryType: categoryType },
        })
      );
    }

    // Wait a moment for DOM updates, then reset category selection
    setTimeout(() => {
      this.resetCategorySelection();
    }, 10);
  }

  saveCategoryType(categoryType) {
    try {
      localStorage.setItem(this.storageKey, categoryType);
    } catch (error) {
      console.warn('AtomCategoriesMegaMenu: Could not save to localStorage:', error);
    }
  }

  loadCategoryType() {
    try {
      const savedType = localStorage.getItem(this.storageKey);
      return savedType;
    } catch (error) {
      console.warn('AtomCategoriesMegaMenu: Could not load from localStorage:', error);
      return null;
    }
  }

  setCategoryType() {
    let button = null;

    // Check if we have a category type in the query string
    if (window.ATOM && window.ATOM.params && window.ATOM.params.cattype) {
      // Find the button for this category type
      button = this.element.querySelector(`[data-category-type="${window.ATOM.params.cattype}"]`);
      if (button) {
        this.switchCategoryType(button);
        return;
      } else {
        console.error(`Unable to find query category type '${window.ATOM.params.cattype}'`);
      }
    }

    // There was no valid value in the query string, so try to load one from localStorage
    const savedType = this.loadCategoryType();
    if (savedType) {
      // Find the button for the saved category type
      button = this.element.querySelector(`[data-category-type="${savedType}"]`);
      if (button) {
        this.switchCategoryType(button, false); // This is already saved
        return;
      } else {
        console.error(`Unable to find saved category type '${savedType}'`);
      }
    }

    // Check if customer metafield config.unspsc or punchout.is_unspsc is set and true
    if (typeof window.ATOM !== 'undefined' && window.ATOM.customer) {
      const customer = window.ATOM.customer;
      const configUnspsc = customer.metafields?.config?.unspsc;
      const punchoutUnspsc = customer.metafields?.punchout?.is_unspsc;

      const isUnspscCustomer = configUnspsc === 'true' || configUnspsc === true || punchoutUnspsc === 'true' || punchoutUnspsc === true;

      if (isUnspscCustomer) {
        // Find the button for the UNSPSC category type
        button = this.element.querySelector(`[data-category-type="unspsc"]`);
        if (button) {
          this.switchCategoryType(button, false); // Don't save this value in case it changes
          return;
        } else {
          console.error(`Unable to find customer category type 'unspsc'`);
        }
      }
    }

    // Default to webcat categories
    const defaultButton = this.element.querySelector(`[data-category-type="webcat"]`);
    if (defaultButton) {
      this.switchCategoryType(defaultButton, false); // Don't save this value because it is just the default
    } else {
      console.error(`Unable to find default category type 'webcat'`);
    }
  }

  showSubcategoriesOnMobile(button) {
    const categoryType = button.closest('[data-category-type]');
    if (!categoryType) return;

    const categoryId = button.dataset.category;

    // Hide categories panel
    const categoriesPanel = categoryType.querySelector('.atom-megamenu__categories');
    if (categoriesPanel) {
      categoriesPanel.style.display = 'none';
    }

    // Show subcategories panel
    const subcategoriesPanel = categoryType.querySelector('.atom-megamenu__subcategories');
    if (subcategoriesPanel) {
      subcategoriesPanel.classList.add('atom-megamenu__subcategories--mobile-visible');
      subcategoriesPanel.style.display = 'block';
    }

    // Show the specific subcategory for this category
    this.showCategory(button);
  }

  hideSubcategoriesOnMobile() {
    // Find all category types
    const categoryTypes = this.element.querySelectorAll('[data-category-type]');
    categoryTypes.forEach((categoryType) => {
      // Show categories panel
      const categoriesPanel = categoryType.querySelector('.atom-megamenu__categories');
      if (categoriesPanel) {
        categoriesPanel.style.display = 'block';
      }

      // Hide subcategories panel
      const subcategoriesPanel = categoryType.querySelector('.atom-megamenu__subcategories');
      if (subcategoriesPanel) {
        subcategoriesPanel.classList.remove('atom-megamenu__subcategories--mobile-visible');
        subcategoriesPanel.style.display = 'none';
      }
    });
  }

  resetMobileView() {
    // Reset all category types to show categories panel
    const categoryTypes = this.element.querySelectorAll('[data-category-type]');
    categoryTypes.forEach((categoryType) => {
      // Ensure categories panel is visible
      const categoriesPanel = categoryType.querySelector('.atom-megamenu__categories');
      if (categoriesPanel) {
        categoriesPanel.style.display = 'block';
      }

      // Ensure subcategories panel is hidden
      const subcategoriesPanel = categoryType.querySelector('.atom-megamenu__subcategories');
      if (subcategoriesPanel) {
        subcategoriesPanel.classList.remove('atom-megamenu__subcategories--mobile-visible');
        subcategoriesPanel.style.display = 'none';
      }
    });
  }
}

// Initialize the megamenu when the DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const megamenuElement = document.querySelector('.atom-megamenu');
  if (megamenuElement) {
    const megamenu = new AtomCategoriesMegaMenu(megamenuElement);
    // Make it globally accessible for debugging
    window.ATOM = window.ATOM || {};
    window.ATOM.categoriesMegaMenu = megamenu;
  }
});

// Also initialize if the element is added dynamically
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const megamenuElement = node.querySelector('.atom-megamenu');
        if (megamenuElement && !megamenuElement.dataset.initialized) {
          megamenuElement.dataset.initialized = 'true';
          new AtomCategoriesMegaMenu(megamenuElement);
        }
      }
    });
  });
});

observer.observe(document.body, {
  childList: true,
  subtree: true,
});
